/**
 * Agent features (owner, 2026-10-15): "instead of just changing the name,
 * think how you can push the personalization of agents further".
 *
 * One partner agency used to be special by NAME (`COMPANY.keyAgent`, "Kuoni"
 * for Asian Trails, "Larkspur" in the demo). Its treatment is now a set of
 * switches any agent row can carry (`agents.features`, jsonb), edited in the
 * agent window. The server resolves them (`GET /coportal/agents/features`,
 * `api/Modules/Coportal/Services/AgentFeatures.cs`); this module holds the
 * same rules for the browser:
 *
 *   switch            what it does here
 *   quickFilter ..... a one-button filter in the Logs toolbar
 *   rowMarker ....... bold agent name + right rail on its Logs rows
 *   welcomeMeetings . (server only: Welcome Meetings to arrange)
 *   courtesyCalls ... Super Transfers' courtesy tab + the dashboard panel
 *   partner ......... owns the partner ref (`kuoni_ref`): its label, the K pill
 *
 * A line is the agent's when the agent column contains its name or an
 * alternate name; `match` words, when set, narrow that for the quick filter,
 * welcome meetings and courtesy calls (Kuoni: "Kuoni", so those stay exactly
 * as they were while the marker keeps every spelling).
 *
 * Asian Trails unchanged: until the set loads, for Agent Portal users (who
 * never load it), and while no agent row carries features, everything runs on
 * `fallbackAgentFeatures()`, today's key-agent rule from `COMPANY.keyAgent`.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { apiFetchRaw } from '@/auth/api';
import { COMPANY, COMPANY_DEFAULTS, type CompanyProfile } from '@/shared/company';
import { parseAliases } from '@/shared/aliases';
import { defaultDestination, destinationCodes, destinationOfSector } from '@/shared/geo/opsGeo';

// ── Types ────────────────────────────────────────────────────────────────

/** A destination code of the tenant's geography (MY, SG, BN for Asian Trails). */
export type Destination = string;
/**
 * Asian Trails' destinations, the courtesy-call picker's historical list.
 * The picker and the parser read {@link destinations} (the tenant's
 * geography, brief §8.4.4 G2); this stays for the fallback rule and tests.
 */
export const DESTINATIONS: readonly Destination[] = ['MY', 'SG', 'BN'];

/** The tenant's destinations, in order (MY, SG, BN for Asian Trails). */
export function destinations(): Destination[] {
  return destinationCodes();
}
/** Words shorter than this are ignored ("AT" would match half the agents). */
export const MIN_TERM_LENGTH = 2;
export const MAX_DAYS_AFTER = 14;

export interface CourtesyCallsFeature {
  on: boolean;
  /** Empty = every destination. */
  destinations: Destination[];
  daysAfter: number;
  exclude: string[];
}

/** One agent with features, as `GET /coportal/agents/features` sends it. */
export interface AgentFeatureEntry {
  id: string;
  name: string;
  shortName: string;
  /** The agent name and its alternate names. */
  names: string[];
  match: string[];
  quickFilter: boolean;
  rowMarker: boolean;
  welcomeMeetings: boolean;
  courtesyCalls: CourtesyCallsFeature | null;
  partner: boolean;
}

export interface AgentFeatureSet {
  /** False while no agent row carries features: the key-agent fallback. */
  configured: boolean;
  agents: AgentFeatureEntry[];
}

/** The fallback agent's id (never a row id). */
export const FALLBACK_AGENT_ID = 'key-agent';

/**
 * Today's key-agent rule (the server's `AgentFeatureSet.Fallback`): the quick
 * filter, the marker (with PHP's second spelling, "DER Touristik UK",
 * `table.js:908`), courtesy calls in Singapore the day after arrival with DER
 * Touristik left out, and the partner ref. The two DER Touristik words are
 * Kuoni's, so they ride only with the default key agent (Asian Trails);
 * another company's key agent (the demo's Larkspur) never shows them.
 */
export function fallbackAgentFeatures(profile: CompanyProfile = COMPANY): AgentFeatureSet {
  const key = profile.keyAgent;
  const kuoni = key === COMPANY_DEFAULTS.keyAgent;
  return {
    configured: false,
    agents: [
      {
        id: FALLBACK_AGENT_ID,
        name: key,
        shortName: key,
        names: kuoni ? [key, 'DER Touristik UK'] : [key],
        match: [key],
        quickFilter: true,
        rowMarker: true,
        welcomeMeetings: true,
        courtesyCalls: { on: true, destinations: ['SG'], daysAfter: 1, exclude: kuoni ? ['DER Touristik'] : [] },
        partner: true,
      },
    ],
  };
}

// ── Lenient readers ──────────────────────────────────────────────────────

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const squash = (s: unknown): string => (typeof s === 'string' ? s.trim().replace(/\s+/g, ' ') : '');

function words(v: unknown): string[] {
  const raw = Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : typeof v === 'string' ? parseAliases(v) : [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const r of raw) {
    const t = squash(r);
    if (t.length < MIN_TERM_LENGTH || seen.has(t.toLowerCase())) continue;
    seen.add(t.toLowerCase());
    out.push(t);
  }
  return out;
}

function courtesyOf(v: unknown): CourtesyCallsFeature | null {
  if (!isObj(v)) return null;
  const dest = (Array.isArray(v.destinations) ? v.destinations : [])
    .map((d) => (typeof d === 'string' ? d.trim().toUpperCase() : ''))
    .filter((d): d is Destination => destinations().includes(d));
  const days = typeof v.daysAfter === 'number' && Number.isFinite(v.daysAfter)
    ? Math.min(MAX_DAYS_AFTER, Math.max(0, Math.trunc(v.daysAfter)))
    : 1;
  return { on: v.on === true, destinations: Array.from(new Set(dest)), daysAfter: days, exclude: words(v.exclude) };
}

/** The server's reply, read leniently; anything unusable = the fallback. */
export function normalizeAgentFeatureSet(raw: unknown, profile: CompanyProfile = COMPANY): AgentFeatureSet {
  if (!isObj(raw) || raw.configured !== true || !Array.isArray(raw.agents)) return fallbackAgentFeatures(profile);
  const agents: AgentFeatureEntry[] = [];
  for (const a of raw.agents) {
    if (!isObj(a)) continue;
    const id = typeof a.id === 'string' ? a.id : '';
    const name = squash(a.name);
    if (!id || !name) continue;
    agents.push({
      id,
      name,
      shortName: squash(a.shortName) || name,
      names: words(a.names).length ? words(a.names) : [name],
      match: words(a.match),
      quickFilter: a.quickFilter === true,
      rowMarker: a.rowMarker === true,
      welcomeMeetings: a.welcomeMeetings === true,
      courtesyCalls: courtesyOf(a.courtesyCalls),
      partner: a.partner === true,
    });
  }
  return { configured: true, agents };
}

// ── Matching (same rules as AgentLineMatch.cs) ───────────────────────────

/** The text contains one of the words, any case. */
export function containsAny(text: unknown, terms: readonly string[]): boolean {
  if (typeof text !== 'string' || !text) return false;
  const hay = text.toLowerCase();
  return terms.some((t) => t.length > 0 && hay.includes(t.toLowerCase()));
}

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** `\bKuoni\b`, any case; spaces inside a word match any spacing (`\bDER\s*Touristik\b`). */
export function wordRe(term: string): RegExp {
  return new RegExp(`\\b${term.trim().split(/\s+/).map(esc).join('\\s*')}\\b`, 'i');
}

/** The text names one of the words as a whole word. */
export function wordAny(text: unknown, terms: readonly string[]): boolean {
  if (typeof text !== 'string' || !text) return false;
  return terms.some((t) => t.trim().length > 0 && wordRe(t).test(text));
}

/** Words a query needs: a word containing another is already found by it ("Kuoni UK" by "Kuoni"). */
export function minimalTerms(terms: readonly string[]): string[] {
  const list: string[] = [];
  const seen = new Set<string>();
  for (const t of terms) {
    const v = t.trim();
    if (!v || seen.has(v.toLowerCase())) continue;
    seen.add(v.toLowerCase());
    list.push(v);
  }
  return list.filter((t) => !list.some((o) => o.toLowerCase() !== t.toLowerCase() && t.toLowerCase().includes(o.toLowerCase())));
}

/** Words the quick filter, welcome meetings and courtesy calls look for. */
export function workTerms(a: AgentFeatureEntry): string[] {
  return a.match.length > 0 ? a.match : a.names;
}

/** Words the row marker looks for: every name and every match word. */
export function markerTerms(a: AgentFeatureEntry): string[] {
  return words([...a.names, ...a.match]);
}

// ── Logs: quick filters and the row marker ───────────────────────────────

export interface QuickFilter {
  id: string;
  /** The button text: the short name in capitals ("KUONI"). */
  label: string;
  /** The owner of the partner ref keeps the old `?kuoni=1` link. */
  legacy: boolean;
  agent: AgentFeatureEntry;
}

export function quickFilters(set: AgentFeatureSet): QuickFilter[] {
  return set.agents
    .filter((a) => a.quickFilter)
    .map((a) => ({ id: a.id, label: a.shortName.toUpperCase(), legacy: a.partner, agent: a }));
}

/** The URL param a button writes: `kuoni=1` for the partner (old links), `qf=<agent id>` otherwise. */
export function quickFilterParam(f: QuickFilter): ['kuoni' | 'qf', string] {
  return f.legacy ? ['kuoni', '1'] : ['qf', f.id];
}

/** The button the URL names: `?qf=<id>`, or `?kuoni=1` for the partner's (bookmarks from before). */
export function activeQuickFilter(
  params: { get(name: string): string | null },
  filters: readonly QuickFilter[],
): QuickFilter | null {
  const qf = params.get('qf');
  if (qf) return filters.find((f) => f.id === qf) ?? null;
  if (params.get('kuoni') === '1') return filters.find((f) => f.legacy) ?? null;
  return null;
}

interface LineLike {
  agent?: unknown;
  supplier?: unknown;
  kuoni_ref?: unknown;
}

/**
 * The quick filter's test: the agent or the supplier names the agent, or (the
 * partner only) the line carries a partner ref. For Kuoni this is exactly the
 * old KUONI button (`agent`/`supplier` contains "kuoni", or `kuoni_ref` set).
 */
export function quickFilterMatches(row: LineLike, f: QuickFilter): boolean {
  const terms = workTerms(f.agent);
  if (f.agent.partner && row.kuoni_ref != null && row.kuoni_ref !== '') return true;
  return containsAny(row.supplier, terms) || containsAny(row.agent, terms);
}

/** Is this agent column one a "mark its lines" agent names? */
export function isMarkedAgent(agent: unknown, set: AgentFeatureSet): boolean {
  return set.agents.some((a) => a.rowMarker && containsAny(agent, markerTerms(a)));
}

// ── Partner ref ──────────────────────────────────────────────────────────

export function partnerAgent(set: AgentFeatureSet): AgentFeatureEntry | null {
  return set.agents.find((a) => a.partner) ?? null;
}

/** "Kuoni": the partner ref's owner, for "Kuoni Ref" and the K pill; "Partner" when none. */
export function partnerName(set: AgentFeatureSet): string {
  return partnerAgent(set)?.shortName ?? 'Partner';
}

/** The pill's letter: "K" for Kuoni. */
export function partnerInitial(set: AgentFeatureSet): string {
  return partnerName(set).charAt(0).toUpperCase();
}

// ── Courtesy calls ───────────────────────────────────────────────────────

export interface CourtesyRule {
  agentId: string;
  agentName: string;
  shortName: string;
  terms: string[];
  destinations: Destination[];
  daysAfter: number;
  exclude: string[];
}

export function courtesyRules(set: AgentFeatureSet): CourtesyRule[] {
  return set.agents
    .filter((a) => a.courtesyCalls?.on)
    .map((a) => ({
      agentId: a.id,
      agentName: a.name,
      shortName: a.shortName,
      terms: workTerms(a),
      destinations: a.courtesyCalls!.destinations,
      daysAfter: a.courtesyCalls!.daysAfter,
      exclude: a.courtesyCalls!.exclude,
    }));
}

/** The default rule (Kuoni, SG, the day after, DER Touristik left out). */
export const DEFAULT_COURTESY_RULES: readonly CourtesyRule[] = courtesyRules(fallbackAgentFeatures());

/**
 * A sector's destination, as the Logs page reads a row's country: SG, BN,
 * else MY for Asian Trails; the tenant's geography generally (the API's
 * `CourtesyCallScope.DestinationOf` says the same).
 */
export function destinationOf(sector: unknown): Destination | null {
  const s = typeof sector === 'string' ? sector.trim().toUpperCase() : '';
  if (!s) return null;
  return destinationOfSector(s) ?? defaultDestination();
}

/** "Kuoni SG", "WydeWays MY & SG", or just the name for every destination. */
export function courtesyRuleLabel(r: CourtesyRule): string {
  return r.destinations.length === 0 ? r.shortName : `${r.shortName} ${r.destinations.join(' & ')}`;
}

// ── The store (one per session) ──────────────────────────────────────────

let current: AgentFeatureSet = fallbackAgentFeatures();
let loading: Promise<void> | null = null;
let loadedAt = 0;
const listeners = new Set<() => void>();
const STALE_MS = 5 * 60 * 1000;

function emit() {
  for (const fn of listeners) fn();
}

export function getAgentFeatures(): AgentFeatureSet {
  return current;
}

export function setAgentFeatures(set: AgentFeatureSet): void {
  if (set === current) return;
  current = set;
  emit();
}

/** Back to the fallback (sign-out, tests). */
export function resetAgentFeatures(): void {
  loading = null;
  loadedAt = 0;
  setAgentFeatures(fallbackAgentFeatures());
}

/**
 * Read the set from the API (staff only; the endpoint refuses Agent Portal
 * users). A failed read keeps what is there: the fallback, i.e. today.
 */
export function loadAgentFeatures(force = false): Promise<void> {
  if (!force && (loading || Date.now() - loadedAt < STALE_MS)) return loading ?? Promise.resolve();
  const run = apiFetchRaw<unknown>('/coportal/agents/features', { method: 'GET' })
    .then((raw) => {
      loadedAt = Date.now();
      setAgentFeatures(normalizeAgentFeatureSet(raw));
    })
    .catch(() => {
      loadedAt = Date.now();
    })
    .finally(() => {
      if (loading === run) loading = null;
    });
  loading = run;
  return run;
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * The current set; re-renders when it changes. `load` (staff screens) reads
 * it from the API once per five minutes; rows and cells pass `false` and
 * follow whatever the page loaded.
 */
export function useAgentFeatures(opts: { load?: boolean } = {}): AgentFeatureSet {
  const load = opts.load ?? false;
  useEffect(() => {
    if (load) void loadAgentFeatures();
  }, [load]);
  return useSyncExternalStore(subscribe, getAgentFeatures, getAgentFeatures);
}

// ── The agent window's form ──────────────────────────────────────────────

export interface AgentFeaturesForm {
  shortName: string;
  match: string[];
  quickFilter: boolean;
  rowMarker: boolean;
  welcomeMeetings: boolean;
  courtesyOn: boolean;
  courtesyDestinations: Destination[];
  courtesyDaysAfter: number;
  courtesyExclude: string[];
  partner: boolean;
}

export const EMPTY_FEATURES_FORM: AgentFeaturesForm = Object.freeze({
  shortName: '',
  match: [],
  quickFilter: false,
  rowMarker: false,
  welcomeMeetings: false,
  courtesyOn: false,
  courtesyDestinations: [],
  courtesyDaysAfter: 1,
  courtesyExclude: [],
  partner: false,
}) as AgentFeaturesForm;

/** An agent row's `features` column as the form. */
export function featuresFormOf(raw: unknown): AgentFeaturesForm {
  if (!isObj(raw)) return { ...EMPTY_FEATURES_FORM };
  const cc = courtesyOf(raw.courtesyCalls);
  return {
    shortName: squash(raw.shortName),
    match: words(raw.match),
    quickFilter: raw.quickFilter === true,
    rowMarker: raw.rowMarker === true,
    welcomeMeetings: raw.welcomeMeetings === true,
    courtesyOn: cc?.on ?? false,
    courtesyDestinations: cc?.destinations ?? [],
    courtesyDaysAfter: cc?.daysAfter ?? 1,
    courtesyExclude: cc?.exclude ?? [],
    partner: raw.partner === true,
  };
}

/** The form as the column stores it (version 1). */
export function featuresPayload(f: AgentFeaturesForm): Record<string, unknown> {
  return {
    v: 1,
    shortName: squash(f.shortName) || null,
    match: words(f.match),
    quickFilter: f.quickFilter,
    rowMarker: f.rowMarker,
    welcomeMeetings: f.welcomeMeetings,
    courtesyCalls: {
      on: f.courtesyOn,
      destinations: destinations().filter((d) => f.courtesyDestinations.includes(d)),
      daysAfter: Math.min(MAX_DAYS_AFTER, Math.max(0, Math.trunc(f.courtesyDaysAfter || 0))),
      exclude: words(f.courtesyExclude),
    },
    partner: f.partner,
  };
}

/** Is any switch on? */
export function anyFeatureOn(f: AgentFeaturesForm): boolean {
  return f.quickFilter || f.rowMarker || f.welcomeMeetings || f.courtesyOn || f.partner;
}

/**
 * What the save sends: nothing when the form did not move (its column waits
 * on a SQL file, and an untouched section should not earn a warning), and
 * nothing for a row that had no features and still has every switch off (it
 * would make the workspace "configured" with nothing on).
 */
export function featuresToSave(
  loadedRaw: unknown,
  form: AgentFeaturesForm,
): Record<string, unknown> | undefined {
  const next = featuresPayload(form);
  const before = isObj(loadedRaw) ? featuresPayload(featuresFormOf(loadedRaw)) : null;
  if (before === null && !anyFeatureOn(form)) return undefined;
  if (before !== null && JSON.stringify(before) === JSON.stringify(next)) return undefined;
  return next;
}
